import {
  CannedResponse,
  CannedResponseCategory,
  CreateCannedResponseRequest,
  UpdateCannedResponseRequest,
  CannedResponseFilters,
  CannedShortcutMatch,
} from '../types/cannedResponse';
import { defaultCannedResponses } from '../data/defaultCannedResponses';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://www.bonyad-hub.com/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// In-memory storage for development (replace with actual API calls)
let mockResponses: CannedResponse[] = defaultCannedResponses.map((r) => ({ ...r }));

const mockCategories: CannedResponseCategory[] = [
  { id: 'greeting', name: 'Greetings', color: '#10b981', responseCount: 3 },
  { id: 'closing', name: 'Closing', color: '#6366f1', responseCount: 2 },
  { id: 'escalation', name: 'Escalation', color: '#f59e0b', responseCount: 2 },
  { id: 'information', name: 'Information Request', color: '#3b82f6', responseCount: 1 },
  { id: 'resolution', name: 'Resolution', color: '#22c55e', responseCount: 1 },
  { id: 'technical', name: 'Technical', color: '#ef4444', responseCount: 0 },
  { id: 'follow-up', name: 'Follow-up', color: '#8b5cf6', responseCount: 0 },
  { id: 'apology', name: 'Apology', color: '#ec4899', responseCount: 0 },
  { id: 'billing', name: 'Billing', color: '#0ea5e9', responseCount: 0 },
  { id: 'security', name: 'Security', color: '#64748b', responseCount: 0 },
  { id: 'outage', name: 'Incidents', color: '#dc2626', responseCount: 0 },
];

export const cannedResponseApi = {
  // Get all canned responses with optional filters
  getAll: async (filters?: CannedResponseFilters): Promise<CannedResponse[]> => {
    try {
      // TODO: Replace with actual API call
      // const response = await fetch(`${API_BASE_URL}/admin/canned-responses?${params}`, {
      //   headers: getAuthHeaders(),
      // });
      // return await response.json();

      let filtered = [...mockResponses];

      if (filters) {
        if (filters.category) {
          filtered = filtered.filter(r => r.category === filters.category);
        }
        if (filters.search) {
          const search = filters.search.toLowerCase();
          const raw = filters.search;
          filtered = filtered.filter(r =>
            r.title.toLowerCase().includes(search) ||
            (r.titleAr && r.titleAr.toLowerCase().includes(search)) ||
            r.titleAr?.includes(raw) ||
            r.content.toLowerCase().includes(search) ||
            (r.contentAr && r.contentAr.toLowerCase().includes(search)) ||
            r.contentAr?.includes(raw) ||
            r.shortcut?.toLowerCase().includes(search) ||
            r.shortcutAr?.toLowerCase().includes(search) ||
            r.tags.some(t => t.toLowerCase().includes(search))
          );
        }
        if (filters.tags && filters.tags.length > 0) {
          filtered = filtered.filter(r => 
            filters.tags!.some(tag => r.tags.includes(tag))
          );
        }
        if (filters.isActive !== undefined) {
          filtered = filtered.filter(r => r.isActive === filters.isActive);
        }
      }

      return filtered.sort((a, b) => b.usageCount - a.usageCount);
    } catch (error) {
      console.error('Failed to fetch canned responses:', error);
      throw error;
    }
  },

  // Get a single canned response by ID
  getById: async (id: string): Promise<CannedResponse | null> => {
    try {
      // TODO: Replace with actual API call
      return mockResponses.find(r => r.id === id) || null;
    } catch (error) {
      console.error('Failed to fetch canned response:', error);
      throw error;
    }
  },

  // Create a new canned response
  create: async (data: CreateCannedResponseRequest): Promise<CannedResponse> => {
    try {
      // TODO: Replace with actual API call
      // const response = await fetch(`${API_BASE_URL}/admin/canned-responses`, {
      //   method: 'POST',
      //   headers: getAuthHeaders(),
      //   body: JSON.stringify(data),
      // });
      // return await response.json();

      const newResponse: CannedResponse = {
        id: Date.now().toString(),
        ...data,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        usageCount: 0,
      };
      mockResponses.push(newResponse);
      return newResponse;
    } catch (error) {
      console.error('Failed to create canned response:', error);
      throw error;
    }
  },

  // Update a canned response
  update: async (id: string, data: UpdateCannedResponseRequest): Promise<CannedResponse> => {
    try {
      // TODO: Replace with actual API call
      // const response = await fetch(`${API_BASE_URL}/admin/canned-responses/${id}`, {
      //   method: 'PUT',
      //   headers: getAuthHeaders(),
      //   body: JSON.stringify(data),
      // });
      // return await response.json();

      const index = mockResponses.findIndex(r => r.id === id);
      if (index === -1) throw new Error('Canned response not found');

      mockResponses[index] = {
        ...mockResponses[index],
        ...data,
        updatedAt: new Date().toISOString(),
      };
      return mockResponses[index];
    } catch (error) {
      console.error('Failed to update canned response:', error);
      throw error;
    }
  },

  // Delete a canned response
  delete: async (id: string): Promise<void> => {
    try {
      // TODO: Replace with actual API call
      // await fetch(`${API_BASE_URL}/admin/canned-responses/${id}`, {
      //   method: 'DELETE',
      //   headers: getAuthHeaders(),
      // });

      mockResponses = mockResponses.filter(r => r.id !== id);
    } catch (error) {
      console.error('Failed to delete canned response:', error);
      throw error;
    }
  },

  // Increment usage count
  incrementUsage: async (id: string): Promise<void> => {
    try {
      const index = mockResponses.findIndex(r => r.id === id);
      if (index !== -1) {
        mockResponses[index].usageCount += 1;
      }
    } catch (error) {
      console.error('Failed to increment usage:', error);
    }
  },

  // Get all categories
  getCategories: async (): Promise<CannedResponseCategory[]> => {
    try {
      // TODO: Replace with actual API call
      return mockCategories.map(cat => ({
        ...cat,
        responseCount: mockResponses.filter(r => r.category === cat.id && r.isActive).length,
      }));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      throw error;
    }
  },

  // Create category
  createCategory: async (name: string): Promise<CannedResponseCategory> => {
    try {
      const newCategory: CannedResponseCategory = {
        id: name.toLowerCase().replace(/\s+/g, '-'),
        name,
        color: '#666',
        responseCount: 0,
      };
      mockCategories.push(newCategory);
      return newCategory;
    } catch (error) {
      console.error('Failed to create category:', error);
      throw error;
    }
  },

  // Search by shortcut (English shortcut → EN body; Arabic shortcut → AR body)
  searchByShortcut: async (shortcut: string): Promise<CannedShortcutMatch | null> => {
    try {
      const en = mockResponses.find(r => r.shortcut === shortcut && r.isActive);
      if (en) return { response: en, language: 'en' };
      const ar = mockResponses.find(r => r.shortcutAr === shortcut && r.isActive);
      if (ar) return { response: ar, language: 'ar' };
      return null;
    } catch (error) {
      console.error('Failed to search by shortcut:', error);
      return null;
    }
  },
};

export default cannedResponseApi;
